'use client';

import { useQuery } from '@tanstack/react-query';
import type { PoolId } from '@/lib/types';
import { RateLimitError } from '@/lib/rate-limit-error';

/**
 * Pool-level fee data from the contract.
 */
export interface PoolFeeData {
  /** Total ETH fees for this pool (includes pending escrow balance) - wei */
  managerFees: bigint;
  /** Total ETH fees already pulled from escrow - wei */
  splitFees: bigint;
  /** Tax rate in basis points (e.g., 500 = 5% per week) */
  taxRateBps: bigint;
  /** Calculated average earnings per seat per week in wei */
  avgEarningsPerSeatWei: bigint;
  /** Total ETH distributed (same as managerFees, for convenience) */
  totalDistributed: bigint;
}

/**
 * Hook to fetch global fee data via cached API endpoint.
 * 
 * SCALABILITY: Uses server-side caching to share data across all users.
 * Instead of each user making direct RPC calls, everyone shares the same
 * cached response. Reduces RPC calls by ~99% at scale.
 * 
 * For "Total Distributed" UI, use `managerFees`.
 * For "Avg Earn/wk", use `avgEarningsPerSeatWei` (calculated as managerFees / poolAgeWeeks / activeSeatCount).
 * 
 * @param activeSeatCount - Number of active seats (to calculate per-seat earnings)
 * @param poolCreatedAt - Timestamp when pool was created (to calculate age)
 * @param enabled - Whether to fetch data (default: true)
 */
export function usePoolFees(
  activeSeatCount: number,
  poolCreatedAt?: number | string,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['pool-fees-cached', activeSeatCount, poolCreatedAt],
    queryFn: async (): Promise<PoolFeeData> => {
      // Fetch from cached API endpoint (shared across all users)
      const res = await fetch('/api/rpc/global-fees');
      if (res.status === 429) throw new RateLimitError();
      if (!res.ok) {
        throw new Error('Failed to fetch global fees');
      }
      
      const { data } = await res.json();
      
      const managerFees = BigInt(data.managerFees);
      const splitFees = BigInt(data.splitFees);
      const taxRateBps = BigInt(data.taxRateBps);

      // Calculate average earnings per seat per week
      // Formula: managerFees / poolAgeWeeks / activeSeatCount
      let avgEarningsPerSeatWei = 0n;
      
      if (activeSeatCount > 0 && poolCreatedAt) {
        const createdAtMs = typeof poolCreatedAt === 'string' 
          ? parseInt(poolCreatedAt) 
          : poolCreatedAt;
        const ageMs = Date.now() - createdAtMs;
        const ageWeeks = ageMs / (7 * 24 * 60 * 60 * 1000);
        
        if (ageWeeks > 0) {
          // Total fees / weeks = weekly fees
          // Weekly fees / seats = per-seat weekly earnings
          const weeklyFees = Number(managerFees) / ageWeeks;
          avgEarningsPerSeatWei = BigInt(Math.floor(weeklyFees / activeSeatCount));
        }
      }

      return {
        managerFees,
        splitFees,
        taxRateBps,
        avgEarningsPerSeatWei,
        totalDistributed: managerFees,
      };
    },
    enabled,
    staleTime: 30000, // 30 seconds - server caches for 30s anyway
    refetchInterval: 45000, // Refetch every 45 seconds (slower since server caches)
  });
}

/**
 * Hook to fetch live pending fees for a specific holder in a specific pool.
 * Routes through a server-cached API endpoint to avoid direct client-side
 * RPC calls that bypass all caching.
 * 
 * @param poolId - The pool ID
 * @param holderAddress - The holder's address
 * @param enabled - Whether to fetch data
 */
export function usePoolPendingFees(
  poolId: PoolId | undefined,
  holderAddress: string | undefined,
  managerAddress: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['pool-pending-fees', poolId, holderAddress, managerAddress],
    queryFn: async (): Promise<bigint> => {
      if (!poolId || !holderAddress || !managerAddress) return 0n;

      try {
        const res = await fetch(
          `/api/rpc/pool-pending-fees?poolId=${poolId}&address=${holderAddress}&managerAddress=${managerAddress}`
        );
        if (res.status === 429) throw new RateLimitError();
        if (!res.ok) throw new Error('Failed to fetch pool pending fees');

        const { data } = await res.json();
        return BigInt(data.pendingFees || '0');
      } catch (error) {
        if (error instanceof RateLimitError) throw error;
        console.error('Error fetching pool pending fees:', error);
        return 0n;
      }
    },
    enabled: enabled && !!poolId && !!holderAddress && !!managerAddress,
    staleTime: 10000,
    refetchInterval: 15000,
  });
}
